/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Tile from '@atlaskit/tile';
import { token } from '@atlaskit/tokens';

import messages from './messages';

export type AccordionCardItem = {
	id: string;
	title: string;
	subtitle?: string;
	icon: JSX.Element;
	content: React.ReactNode;
};

const styles = cssMap({
	card: {
		overflow: 'hidden',
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
	},
	cardFirst: {
		overflow: 'hidden',
		borderTop: 'none',
	},
	cardHeader: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	cardContent: {
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
		paddingBottom: token('space.100'),
		backgroundColor: token('color.background.neutral.subtle'),
	},
	cardHeaderText: {
		flex: 1,
	},
	cardFooter: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
});

export const AccordionCard = ({
	item,
	isExpanded,
	isExpandable = true,
	onToggle,
	isFirst,
	children,
}: {
	item: AccordionCardItem;
	isExpanded: boolean;
	isExpandable?: boolean;
	onToggle: (id: string) => void;
	isFirst: boolean;
	children?: React.ReactNode;
}): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={isFirst ? styles.cardFirst : styles.card} testId={`accordion-card-${item.id}`}>
			<Box xcss={styles.cardHeader} testId={`accordion-card-header-${item.id}`}>
				<Inline space="space.100" alignBlock="center" xcss={styles.cardHeaderText}>
					<Box>
						<Inline space="space.100" alignBlock="center">
							<Tile label={item.title}>{item.icon}</Tile>

							<Stack>
								<Text>{item.title}</Text>
								{item.subtitle && (
									<Text size="small" color="color.text.subtlest">
										{item.subtitle}
									</Text>
								)}
							</Stack>
						</Inline>
					</Box>
				</Inline>
				{isExpandable && (
					<IconButton
						appearance="subtle"
						icon={(iconProps) =>
							isExpanded ? (
								<ChevronDownIcon {...iconProps} size="small" />
							) : (
								<ChevronRightIcon {...iconProps} size="small" />
							)
						}
						label={isExpanded ? formatMessage(messages.collapse) : formatMessage(messages.expand)}
						onClick={(e) => {
							e.stopPropagation();
							onToggle(item.id);
						}}
						testId={`accordion-card-toggle-${item.id}`}
					/>
				)}
			</Box>
			{isExpanded && (
				<Box xcss={styles.cardContent} testId={`accordion-card-content-${item.id}`}>
					{item.content}
				</Box>
			)}
			{children && <Box xcss={styles.cardFooter}>{children}</Box>}
		</Box>
	);
};
